import { useState, useMemo, useCallback } from 'react';
import type { FilamentQuery, TenantFilamentWithDetails } from '@maker-toolchest/shared';
import { SearchInput } from '@/components/SearchInput';
import { Select } from '@/components/Select';
import { Spinner } from '@/components/Spinner';
import { Pagination } from '@/components/Pagination';
import { FilterBar } from '@/components/FilterBar';
import {
  useFilamentLibrary,
  useFilamentLookups,
  useAddFilamentToLibrary,
  useTenantFilaments,
} from '../hooks';
import { FilamentCard } from './FilamentCard';

interface FilamentLibraryBrowserProps {
  onFilamentSelect?: (filamentId: string) => void;
}

export function FilamentLibraryBrowser({ onFilamentSelect }: FilamentLibraryBrowserProps) {
  const [filters, setFilters] = useState<Partial<FilamentQuery>>({
    page: 1,
    limit: 20,
    includeSystem: true,
    includeCustom: true,
  });
  const [searchKey, setSearchKey] = useState(0);

  const { data, isLoading } = useFilamentLibrary(filters, {
    placeholderData: (previousData) => previousData,
  });
  const { types, manufacturers, isLoading: lookupsLoading } = useFilamentLookups();
  const { data: tenantFilamentsData } = useTenantFilaments();
  const addToLibrary = useAddFilamentToLibrary();

  const filaments = data?.items || [];
  const pagination = data?.pagination;

  // Build set of filament IDs already in tenant's library
  const tenantFilamentIds = new Set(
    tenantFilamentsData?.items?.map((tf: TenantFilamentWithDetails) => tf.filamentId) || []
  );

  const handleSearch = useCallback((search: string) => {
    setFilters((prev) => ({ ...prev, search: search || undefined, page: 1 }));
  }, []);

  const handleFilterChange = useCallback((key: keyof FilamentQuery, value: string | undefined) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value || undefined,
      page: 1,
    }));
  }, []);

  const handleAddToLibrary = useCallback(async (filamentId: string) => {
    try {
      await addToLibrary.mutateAsync({ filamentId });
    } catch {
      // Error handled by mutation
    }
  }, [addToLibrary]);

  const handlePageChange = useCallback((page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters({
      page: 1,
      limit: 20,
      includeSystem: true,
      includeCustom: true,
    });
  }, []);

  const handleApplyFilters = useCallback((newFilters: Record<string, any>) => {
    // Set all filters including search
    setFilters(prev => ({
      ...prev,
      search: newFilters.search || undefined,
      typeId: newFilters.typeId || undefined,
      manufacturerId: newFilters.manufacturerId || undefined,
      diameter: newFilters.diameter ? parseFloat(newFilters.diameter) : undefined,
      page: 1,
    }));

    // Force remount of SearchInput to clear stale debounced state
    setSearchKey(prev => prev + 1);
  }, []);

  const hasActiveFilters = !!(
    filters.search ||
    filters.typeId ||
    filters.manufacturerId ||
    filters.diameter
  );

  // Build options for selects - memoized to prevent unnecessary re-renders
  const typeOptions = useMemo(
    () => [
      { value: '', label: 'All Types' },
      ...types.map((type) => ({ value: type.id, label: type.name })),
    ],
    [types]
  );

  const manufacturerOptions = useMemo(
    () => [
      { value: '', label: 'All Manufacturers' },
      ...manufacturers.map((mfr) => ({ value: mfr.id, label: mfr.name })),
    ],
    [manufacturers]
  );

  const diameterOptions = useMemo(
    () => [
      { value: '', label: 'All' },
      { value: '1.75', label: '1.75mm' },
      { value: '2.85', label: '2.85mm' },
    ],
    []
  );

  if (isLoading && !data) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <FilterBar
        searchField={
          <SearchInput
            key={searchKey}
            value={filters.search || ''}
            onChange={handleSearch}
            placeholder="Search filaments..."
          />
        }
        filterFields={[
          <div className="w-40">
            <Select
              label="Type"
              value={filters.typeId || ''}
              onChange={(e) => handleFilterChange('typeId', e.target.value)}
              disabled={lookupsLoading}
              options={typeOptions}
            />
          </div>,
          <div className="w-48">
            <Select
              label="Manufacturer"
              value={filters.manufacturerId || ''}
              onChange={(e) => handleFilterChange('manufacturerId', e.target.value)}
              disabled={lookupsLoading}
              options={manufacturerOptions}
            />
          </div>,
          <div className="w-32">
            <Select
              label="Diameter"
              value={filters.diameter ? String(filters.diameter) : ''}
              onChange={(e) =>
                handleFilterChange('diameter', e.target.value ? e.target.value : undefined)
              }
              options={diameterOptions}
            />
          </div>,
        ]}
        filterConfigs={[
          { key: 'typeId', label: 'Type', type: 'select', options: typeOptions, disabled: lookupsLoading },
          { key: 'manufacturerId', label: 'Manufacturer', type: 'select', options: manufacturerOptions, disabled: lookupsLoading },
          { key: 'diameter', label: 'Diameter', type: 'select', options: diameterOptions },
        ]}
        filterValues={{
          search: filters.search || '',
          typeId: filters.typeId || '',
          manufacturerId: filters.manufacturerId || '',
          diameter: filters.diameter ? String(filters.diameter) : '',
        }}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
        hasActiveFilters={hasActiveFilters}
      />

      {/* Results */}
      {filaments.length === 0 ? (
        <div className="rounded-lg border border-border bg-background-subtle p-8 text-center">
          <p className="text-foreground-muted">No filaments found matching your criteria.</p>
        </div>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filaments.map((filament) => (
              <FilamentCard
                key={filament.id}
                filament={filament}
                isInLibrary={tenantFilamentIds.has(filament.id)}
                onAdd={() => handleAddToLibrary(filament.id)}
                onView={onFilamentSelect ? () => onFilamentSelect(filament.id) : undefined}
                loading={addToLibrary.isPending && addToLibrary.variables?.filamentId === filament.id}
              />
            ))}
          </div>

          {pagination && pagination.totalPages > 1 && (
            <Pagination
              page={pagination.page}
              total={pagination.total}
              limit={pagination.limit}
              onPageChange={handlePageChange}
            />
          )}
        </>
      )}
    </div>
  );
}
